import { useState, useCallback } from 'react';
import { getApiClient } from 'utils/apiClient';
import { isPartnerTenant } from 'utils/tenantUtils';
import { INITIAL_FORM_DATA } from 'utils/constants';

/**
 * Custom hook for managing resource form state and handlers
 */
export const useResourceForm = ({
  mode,
  resourceId,
  initialMode,
  isDevTenant,
  isSelectedTenantPartner,
  navigate,
  isDevResource,
  setIsDevTenant,
  setIsSelectedTenantPartner,
  setIsDevResource,
  tenants,
  products,
  loadCostPools,
  loadPrices,
  loadResourceData,
  onClose,
  onSuccess,
  setError,
  setLoading,
  isCurrentProductFreeFn,
  isCostPoolRequiredFn,
  formData,
  setFormData
}) => {
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Check if tenant changed and update dev tenant and partner status
    if (name === 'tenant_id') {
      loadCostPools(value);
      // Check if tenant is partner or dev
      const selectedTenant = tenants.find(t => t.id === value);
      if (selectedTenant) {
        const isPartner = isPartnerTenant(selectedTenant);
        setIsSelectedTenantPartner(isPartner);
        setIsDevTenant(selectedTenant.is_dev_tenant || false);
        
        // Reload prices if product is already selected - pass explicit tenant ID to avoid race condition
        if (formData.configuration?.product_id && mode === 'create' && loadPrices) {
          const tenantId = formData.tenant_id || (tenants.length > 0 ? tenants[0].id : null);
          if (tenantId) {
            loadPrices(formData.configuration.product_id, tenantId);
          }
        }
      } else {
        setIsSelectedTenantPartner(false);
        setIsDevTenant(false);
        setIsDevResource(false); // Reset dev resource when tenant changes
      }
    }
  }, [tenants, loadCostPools, loadPrices, mode, formData.configuration?.product_id, setIsSelectedTenantPartner, setIsDevTenant, setIsDevResource]);

  const handleProductSelect = useCallback((productOrId) => {
    // Support both product object (from marketplace) and productId string (backward compatibility)
    const product = typeof productOrId === 'string' 
      ? products.find(p => p.id === productOrId)
      : productOrId;
    
    if (!product) {
      console.warn('Product not found:', productOrId);
      return;
    }
    
    const productId = product.id;
    
    // Get resource_type from product metadata if available
    const metadata = product.metadata || {};
    const marketplace = product.marketplace || {};
    const resourceTypeFromMetadata = metadata.resource_type || marketplace.resourceType;
    
    // Use resource_type from metadata, otherwise use current type or default to 'bot'
    const suggestedType = resourceTypeFromMetadata || formData.type || 'bot';
    
    // Check if the selected product has cost_pool: false in metadata
    // Support both old metadata format and new marketplace format
    const shouldClearCostPool = metadata.cost_pool === false || marketplace.costPool === false;
    
    setFormData(prev => ({
      ...prev,
      type: suggestedType, // Use resource_type from metadata if available
      cost_pool_id: shouldClearCostPool ? '' : prev.cost_pool_id, // Clear cost pool if product doesn't require it
      configuration: {
        ...prev.configuration,
        product_id: productId,
        product_name: product.name || '', // Include product name for backend matching
        price_lookup_key: '' // Reset subscription when product changes
      }
    }));
    
    // Standardized metadata: product.metadata.allow_dev_resource (preferred) or dev_resource (backward compat)
    // Controls whether dev resource toggle is shown/usable for this product
    const allowDevResourceMeta = metadata.allow_dev_resource !== undefined 
      ? metadata.allow_dev_resource 
      : (metadata.dev_resource || marketplace.devResource);
    const devResourceAllowed = allowDevResourceMeta !== false && allowDevResourceMeta !== 'false';
    
    // Standardized metadata: product.metadata.partner_only
    // Check if product is partner-only (no hardcoded product ID checks)
    const isPartnerOnlyProduct = metadata.partner_only === true || metadata.partner_only === 'true';
    
    // Auto-select free pricing for partner tenants or dev resources
    // BUT NOT for partner-only products (they should only show partner_wholesale prices)
    if ((isSelectedTenantPartner || isDevResource) && !isPartnerOnlyProduct) {
      // Wait for prices to load, then auto-select free price
      setTimeout(() => {
        const freePriceKey = `${productId}-partner-free`;
        setFormData(prev => ({
          ...prev,
          configuration: {
            ...prev.configuration,
            price_lookup_key: freePriceKey
          }
        }));
      }, 100);
    }
    
    // If product doesn't allow dev resources, force isDevResource to false
    if (!devResourceAllowed && isDevResource) {
      console.log('🚫 Product does not allow dev resources, disabling dev resource:', productId);
      setIsDevResource(false);
    }
    
    // Load prices for the selected product - pass explicit tenant ID to avoid race condition
    if (loadPrices) {
      const tenantId = formData.tenant_id || null;
      loadPrices(productId, tenantId);
    }
  }, [formData.type, formData.tenant_id, products, isSelectedTenantPartner, isDevResource, loadPrices]);

  const handleClose = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setExpandedFeatures({});
    setIsDevTenant(false);
    setIsSelectedTenantPartner(false);
    setIsDevResource(false);
    setDropdownOpen(false);
    onClose();
  }, [onClose, setIsDevTenant, setIsSelectedTenantPartner, setIsDevResource, setFormData]);

  const handleSubscriptionSelect = useCallback((priceIdOrPrice) => {
    setFormData(prev => {
      // Handle both price object and price ID string
      let priceKey = '';
      if (typeof priceIdOrPrice === 'string') {
        priceKey = priceIdOrPrice;
      } else if (priceIdOrPrice && typeof priceIdOrPrice === 'object') {
        // It's a price object - use lookup_key or id
        priceKey = priceIdOrPrice.lookup_key || priceIdOrPrice.id || '';
      }

      // Do not clear cost pool when free/dev price is selected — user may keep a cost pool for display or when switching back to paid
      return {
        ...prev,
        configuration: {
          ...prev.configuration,
          price_lookup_key: priceKey
        }
      };
    });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validate required fields
      if (mode === 'create') {
        if (!formData.name || !formData.name.trim()) {
          throw new Error('Resource name is required');
        }
        if (!formData.tenant_id || !formData.tenant_id.trim()) {
          throw new Error('Please select a tenant from the dropdown');
        }
        
        // Check if creating a resale resource - only one per tenant allowed
        if (formData.type === 'resale') {
          const apiClient = getApiClient();
          try {
            const resourcesRes = await apiClient.listResources({ workspaceId: formData.tenant_id });
            const existingResaleResources = (resourcesRes.data || []).filter(r => r.type === 'resale');
            if (existingResaleResources.length > 0) {
              throw new Error('Only one resale resource per tenant is allowed. This tenant already has a resale resource connected.');
            }
          } catch (listError) {
            const status = listError?.status || listError?.response?.status;
            if (status === 403) {
              // Create is still backend-authorized and validated; do not block on a preflight read failure.
              console.warn('Resale uniqueness precheck skipped due to forbidden listResources call', {
                tenant_id: formData.tenant_id,
                status
              });
            } else {
              throw listError;
            }
          }
        }
        
        // Backend requires cost_pool_id for ALL products unless it's a dev resource
        // So we validate it for all cases except dev resources
        if (!isDevResource && (!formData.cost_pool_id || !formData.cost_pool_id.trim())) {
          throw new Error('Please select a cost pool');
        }
        if (!formData.configuration?.product_id) {
          throw new Error('Please select a product');
        }
        // Skip price validation for free products, partner tenants, and dev resources
        // Standardized: free products have unit_amount === 0, or are dev resources
        const priceKey = String(formData.configuration?.price_lookup_key || '');
        const isFreeProduct = (priceKey && priceKey.endsWith && priceKey.endsWith('-partner-free')) ||
                            isSelectedTenantPartner || // Partner tenants get free pricing
                            isDevResource; // Dev resources are free
        
        if (!isFreeProduct && formData.type !== 'resale' && !formData.configuration?.price_lookup_key) {
          throw new Error('Please select a subscription plan');
        }
      }
      
      // Remove cost_pool_id ONLY for dev resources
      // ALL other cases (dev tenants, partner tenants, free products, etc.) need cost_pool_id
      // because billing integration is ALWAYS required (backend enforces this)
      const submitData = { ...formData };
      if (isDevResource) {
        delete submitData.cost_pool_id;
      }
      
      // Add is_dev_resource flag if toggle is enabled
      if (isDevResource) {
        submitData.is_dev_resource = true;
      }
      
      const apiClient = getApiClient();
      const response = mode === 'create' 
        ? await apiClient.post('/v1/resources', submitData)
        : await apiClient.put(`/v1/resources/${resourceId}`, submitData);

      if (response.success) {
        // Navigate to appropriate page based on resource type after creation
        if (mode === 'create' && navigate) {
          const createdResource = response.data || response.resource;
          const resourceType = createdResource?.type || formData.type;
          const createdResourceId = createdResource?.id || response.id;
          
          if (createdResourceId) {
            if (resourceType === 'bot') {
              navigate(`/bot-management?resource_id=${createdResourceId}`);
            } else if (resourceType === 'resale') {
              navigate(`/resale-management?resource_id=${createdResourceId}`);
            } else if (resourceType === 'fin_edu') {
              navigate(`/fin-edu-management?resource_id=${createdResourceId}`);
            } else if (resourceType === 'odoo') {
              navigate(`/hosted-app-management?resource_id=${createdResourceId}`);
            }
          }
        }
        
        // Call onSuccess callback if provided (for refreshing dashboard data)
        if (onSuccess && mode === 'create') {
          onSuccess('resources');
        }
        if (mode !== 'create' && loadResourceData) {
          await loadResourceData();
        }
        handleClose();
        // Optionally trigger a callback to refresh parent data
        if (window.refreshResources) {
          window.refreshResources();
        }
      } else {
        throw new Error(response.error || 'Failed to save resource');
      }
    } catch (err) {
      console.error('Error saving resource:', err);
      setError(`Validation Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [
    mode,
    formData,
    resourceId,
    isDevTenant,
    isSelectedTenantPartner,
    isDevResource,
    isCurrentProductFreeFn,
    isCostPoolRequiredFn,
    loadResourceData,
    onSuccess,
    setError,
    setLoading,
    navigate,
    handleClose
  ]);

  const handleDelete = useCallback(async () => {
    if (!resourceId) return;
    
    try {
      setLoading(true);
      const apiClient = getApiClient();
      await apiClient.deleteResource(resourceId);
      
      // Close modal and refresh parent
      handleClose();
      
      // Show success message or trigger refresh
      if (window.parent && window.parent.postMessage) {
        window.parent.postMessage({ type: 'RESOURCE_DELETED', resourceId }, '*');
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
      setError('Failed to delete resource. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [resourceId, setLoading, setError, handleClose]);

  const toggleFeaturesExpansion = useCallback((productId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  }, []);

  return {
    expandedFeatures,
    dropdownOpen,
    setDropdownOpen,
    handleInputChange,
    handleProductSelect,
    handleSubscriptionSelect,
    handleSubmit,
    handleDelete,
    handleClose,
    toggleFeaturesExpansion
  };
};

